import authorizedInstance from '../../../core/api/authorizedInstance';
import { store } from '../../../core/store';
import {
  CreatePaymentIntentResponse,
  CreateStripeCustomerResponse,
  GetSubscriptionProducts,
  ValidatePromoCodeResponse,
} from '../types/subscription';

export const PAYMENT_BASE_API_ENDPOINT = '/api/v1/subscriptions';

export const getSubscriptionProducts = ({ params }: { params: any }, signal?: AbortSignal) => {
  return authorizedInstance.get<GetSubscriptionProducts>(
    `${PAYMENT_BASE_API_ENDPOINT}/products?${params.toString()}`,
    { signal }
  );
};

export const createStripeCustomer = (signal?: AbortSignal) => {
  return authorizedInstance.post<CreateStripeCustomerResponse>(
    `${PAYMENT_BASE_API_ENDPOINT}/add-customer`,
    {},
    { signal }
  );
};

export const createPaymentIntent = (
  priceId: string,
  paymentMethodId: string,
  isAddOn: boolean,
  isRefCodeAvailable?: boolean,
  promoCode?: string,
  referralDiscountPercent?: number,
  referralCode?: string,
  trialPeriodDays?: number,
  signal?: AbortSignal
) => {
  return authorizedInstance.post<CreatePaymentIntentResponse>(
    `${PAYMENT_BASE_API_ENDPOINT}/payment-intent`,
    {
      priceId,
      paymentMethodId,
      isAddOn,
      isRefCodeAvailable,
      promoCode,
      referralDiscountPercent,
      referralCode,
      trialPeriodDays,
    },
    { signal }
  );
};

export const updatePaymentIntent = (
  clientSecret: string,
  paymentId?: string | null,
  signal?: AbortSignal
) => {
  const params = new URLSearchParams();
  if (paymentId) {
    params.append('paymentId', paymentId);
  }
  return authorizedInstance.patch<CreatePaymentIntentResponse>(
    `${PAYMENT_BASE_API_ENDPOINT}/payment-intent?${params.toString()}`,
    { clientSecret },
    { signal }
  );
};

export const createPaymentIntentWithPaymentId = (
  priceId: string,
  paymentMethodId: string,
  isAddOn: boolean,
  paymentId: string,
  isRefCodeAvailable?: boolean,
  promoCode?: string,
  referralDiscountPercent?: number,
  referralCode?: string,
  trialPeriodDays?: number,
  signal?: AbortSignal
) => {
  const params = new URLSearchParams();
  params.append('paymentId', paymentId);

  return authorizedInstance.patch<CreatePaymentIntentResponse>(
    `${PAYMENT_BASE_API_ENDPOINT}/payment-intent?${params.toString()}`,
    {
      priceId,
      paymentMethodId,
      isAddOn,
      isRefCodeAvailable,
      promoCode,
      referralDiscountPercent,
      referralCode,
      trialPeriodDays,
    },
    { signal }
  );
};

export const validatePromoCode = (code: string, signal?: AbortSignal) => {
  const state = store.getState();
  const rawToken = state.auth?.token || '';
  const params = new URLSearchParams();
  if (rawToken) {
    params.append('access_token', rawToken);
  }

  const url = `${PAYMENT_BASE_API_ENDPOINT}/validate-promo-code${
    params.toString() ? `?${params.toString()}` : ''
  }`;

  return authorizedInstance.post<ValidatePromoCodeResponse>(url, { code }, { signal });
};

export interface RedeemPromoCodePayload {
  code: string;
  amount: number; // assumed dollars based on provided payload
  reason?: string;
  metadata?: Record<string, any>;
}

export const redeemPromoCode = (payload: RedeemPromoCodePayload, signal?: AbortSignal) => {
  const state = store.getState();
  const rawToken = state.auth?.token || '';
  const params = new URLSearchParams();
  if (rawToken) {
    params.append('access_token', rawToken);
  }

  const url = `${PAYMENT_BASE_API_ENDPOINT}/redeem-gift-card${
    params.toString() ? `?${params.toString()}` : ''
  }`;

  return authorizedInstance.post<ValidatePromoCodeResponse>(url, payload, { signal });
};

export interface ValidateCardPayload {
  code: string;
  amount: number; // base price after other discounts (dollars)
  reason?: string;
  metadata?: Record<string, any>;
}

export interface ValidateCardResponse extends GetSubscriptionProducts {
  // Overload ApiResponse shape with a minimal data we depend on
  // Some backends might return cents; handle both
  data: {
    price?: number;
    priceCents?: number;
    currency?: string;
  };
}

export const validateCardPrice = (payload: ValidateCardPayload, signal?: AbortSignal) => {
  const state = store.getState();
  const rawToken = state.auth?.token || '';
  const params = new URLSearchParams();
  if (rawToken) {
    params.append('access_token', rawToken);
  }

  const url = `${PAYMENT_BASE_API_ENDPOINT}/validate-promo-code${
    params.toString() ? `?${params.toString()}` : ''
  }`;

  return authorizedInstance.post<ValidateCardResponse>(url, payload, { signal });
};

export const redeemGiftCard = (
  code: string,
  amount: number,
  reason: string,
  metadata: Record<string, any> | undefined,
  accessToken?: string,
  signal?: AbortSignal
) => {
  const query = accessToken ? `?access_token=${encodeURIComponent(accessToken)}` : '';
  return authorizedInstance.post(
    `${PAYMENT_BASE_API_ENDPOINT}/redeem-gift-card${query}`,
    { code, amount, reason, metadata },
    { signal }
  );
};

export interface TrialEligibilityResponse {
  statusCode: number;
  message: string;
  data: {
    trialEligible: boolean;
    trialUsed: boolean;
  };
}

export const getTrialEligibility = (memberId: string, signal?: AbortSignal) => {
  return authorizedInstance.get<TrialEligibilityResponse>(
    `/api/v1/accounts/member/${memberId}/trial-eligibility`,
    { signal }
  );
};
